import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { 
  ETHERNET_PRODUCT_STRING,
  NAVIGATION_EVENTS,
  USER_EVENTS,
  masks,
  MASK,
} from "../assets/const/eventConstants";

import { 
  getPageLoadtime,
  getVenueDetailsFromUrl,
  getBrowserAndOS, 
  getCombinedUrlSearchParams
} from "../utils/eventUtils";
import eventBus from "../utils/eventBus";
import { getOrCreateGsid } from "../utils/utils";
import { eventApiLogger } from "../services/apiServices";
import transformTitleTypeAndMapUrl from "../utils/transformMapTitle";

const logEventApi = (data) => {
  const { browser, browserVersion, os } = getBrowserAndOS();
  const { subdomain, venueId, path  } = getVenueDetailsFromUrl();
  const urlSearchParams = getCombinedUrlSearchParams();
  const { title: pageName, type: pageType } = transformTitleTypeAndMapUrl();

  const eventData = {
    gsid: getOrCreateGsid(),
    mac: urlSearchParams.get("mac"),
    event: "onLoad",
    browser: browser,
    browser_version: browserVersion,
    event_level: "analytics",
    event_source: "frontend",
    event_type: "page_hit",
    location: window.location.href,
    query_params: {
      ...Object.fromEntries(urlSearchParams.entries()),
    },
    on_load_ms: getPageLoadtime(),
    os: os,
    path: path || "/",
    page_name: pageName,
    page_type: pageType,
    product: ETHERNET_PRODUCT_STRING,
    referrer: document.referrer,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    venue: venueId ?? "",
    subdomain: subdomain ?? "",
    user_agent: navigator.userAgent,
    ...data,
  };
  if (masks) {
    const masksList = masks;
    masksList.forEach((mask) => {
      if (eventData && eventData[mask]) {
        (eventData)[mask] = MASK;
      }
    });
  }
  eventApiLogger({ ...eventData });
};

// Define event handlers with their corresponding event types
const createEventHandlers = () => {
  const handlers = [
    // Signup events
    {
      eventType: USER_EVENTS.USER_SIGNUP,
      handler: (event) => {
        const customEvent = event;
        const signupData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "analytics",
          event_details: "user_signup_success",
          username: signupData.username,
          password: signupData.password,
          venue: signupData.venue_id,
        });
      },
    },
    {
      eventType: USER_EVENTS.USER_SIGNUP_ERROR,
      handler: (event) => {
        const customEvent = event;
        const errorData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "error",
          event_details: "user_signup_error",
          error_message: errorData.error,
          venue: errorData.venue_id,
        });
      },
    },
    // Add more event handlers here as needed
    // Example:
    // {
    //   eventType: USER_EVENTS.USER_LOGOUT,
    //   handler: (event: Event) => {
    //     const customEvent = event as CustomEvent;
    //     logEventApi({
    //       event_type: "auth",
    //       event_level: "analytics",
    //       event_details: "user_logout",
    //     });
    //   }
    // },
  ];
  return handlers;
};

const EventAPI = () => {
  const location = useLocation();
  const previousPathRef = useRef(null);

  // Listen for all defined events
  useEffect(() => {
    const eventHandlers = createEventHandlers();

    // Register all event handlers
    eventHandlers.forEach(({ eventType, handler }) => {
      eventBus.on(eventType, handler);
    });

    // Clean up all event handlers
    return () => {
      eventHandlers.forEach(({ eventType, handler }) => {
        eventBus.remove(eventType, handler);
      });
    };
  }, []);

  // Simplified route change and initial load tracking
  useEffect(() => {
    const { path } = getVenueDetailsFromUrl();

    // A timeout is used to ensure that other side effects, such as `react-helmet`
    // updating the document title, have completed before we log the event.
    const timerId = setTimeout(() => {
      // Log initial page load if this is the first render
      if (previousPathRef.current === null) {
        logEventApi({
          product: ETHERNET_PRODUCT_STRING,
        });
      }
      // Log route change if path has changed
      else if (previousPathRef.current !== path) {
        logEventApi({
          event: "routeChange",
          event_type: NAVIGATION_EVENTS.ROUTE_CHANGE,
          previous_path: previousPathRef.current || "/",
          product: ETHERNET_PRODUCT_STRING,
        });
      }
      // Update the previous path reference after logging
      previousPathRef.current = path;
    }, 0);

    return () => clearTimeout(timerId);
  }, [location]);

  return null;
};

export default EventAPI;
